import type { ReactNode } from 'react';
import type { PropsWithClassName, PropsWithRefObject } from '@kc-console/common-ui';
import type { ValueOf } from '@kc-console/core';

const EMPTY_STATE = {
  LOADING: 'loading',
  LIST: 'list',
  SEARCH: 'search',
  WARNING: 'warning',
} as const;

type EmptyStateType = ValueOf<typeof EMPTY_STATE>;

type EmptyStateSize = 'md' | 'lg';

type EmptyStateConfig = {
  /**
   * 테이블의 데이터가 비어 있을 때, 표시할 버튼 혹은 링크를 설정합니다.
   * [링크 바로가기] 링크를 예시로 이용할 수 있습니다.
   */
  action?: ReactNode;
  /**
   * 테이블에서 제안하는 텍스트를 설정합니다. 새로고침 또는 사용자 가이드 링크를 제공할 수 있습니다.
   *
   * @default
   * type: "list" => undefined
   * type: "search" => undefined
   * type: "warning" => "새로고침을 클릭하여 다시 시도해주세요."
   */
  guideText?: ReactNode;
  /**
   * 어떤 이미지를 표시할 지 설정합니다. 미지정시, `type`에 따라 기본 이미지가 지정됩니다.
   */
  illustration?: ReactNode;
  /**
   * 테이블 상태를 나타내는 메시지를 설정합니다. 미지정시, `type`에 따라 기본 메시지가 지정됩니다.
   *
   * @default
   * type: "list" => "생성된 리소스가 없습니다."
   * type: "search" => "일치하는 항목이 없습니다."
   * type: "warning" => "데이터를 불러오는 중 일시적인 오류가 발생하였습니다."
   */
  message?: ReactNode;
};

type WarningEmptyStateConfig = {
  /**
   * type: "warning" 일 때, 새로고침 버튼 클릭 시, 테이블을 다시 불러오는 콜백 함수를 설정합니다.
   * 설정하지 않을 경우 새로고침 버튼은 표시되지 않습니다.
   */
  onRefetch?: () => void;
};

interface EmptyStateProps
  extends PropsWithClassName,
    PropsWithRefObject<HTMLDivElement>,
    EmptyStateConfig,
    WarningEmptyStateConfig {
  /**
   * 테이블의 데이터가 비어 있을 때, 어떤 내용이 표시될지를 설정합니다.
   *
   * - `list`: 데이터가 하나도 없는 경우 (필터가 설정되지 않음)
   * - `search`: 데이터가 하나도 없는 경우 (필터가 설정됨)
   * - `warning`: 데이터를 불러오는 중 에러가 발생한 경우
   */
  type: EmptyStateType;
  /**
   * 일러스트 크기를 설정합니다.
   *
   * - `lg`: 100px. 데이터테이블 기본 사이즈
   * - `md`: 72px. 데이터테이블 min size 및 차트 등 컴포넌트 내부 empty state
   *
   * @default "lg"
   */
  size?: EmptyStateSize;
}

export { EMPTY_STATE };
export type {
  EmptyStateType,
  EmptyStateSize,
  EmptyStateConfig,
  EmptyStateProps,
  WarningEmptyStateConfig,
};
